import React, { useEffect, useMemo, useState } from 'react';
import { getCategoryColor } from '../../utils/categories';
import { withAlpha } from '../../utils/sectionSurface';
import { formatCurrency } from '../../utils/formatCurrency';
import {
  buildCategoryForest,
  countChildren,
  type HierarchyNode,
} from '../../utils/modificationHierarchy';
import type { VehicleDetailModification } from './types';
import { ModThumb } from './ModThumb';
import { CategoryIcon } from '../ui/CategoryIcon';

export interface ModificationNestedListProps {
  modifications: VehicleDetailModification[];
  /** Full unfiltered build — used for select-all-in-assembly counts */
  allModifications?: VehicleDetailModification[];
  showPricing?: boolean;
  isOwner?: boolean;
  onEdit?: (modification: VehicleDetailModification) => void;
  onDelete?: (id: string) => void;
  onMove?: (modification: VehicleDetailModification) => void;
  onAddInside?: (modification: VehicleDetailModification) => void;
  onReorderSibling?: (args: {
    parentId: string | null;
    orderedIds: string[];
  }) => void;
  onExport?: () => void;
  selectMode?: boolean;
  onSelectModeChange?: (on: boolean) => void;
  selectedIds?: Set<string>;
  onSelectedIdsChange?: (ids: Set<string>) => void;
  onBulkMove?: () => void;
  onBulkCategory?: () => void;
  onBulkDelete?: () => void;
  onBulkExport?: () => void;
  /** Reveal matching categories and ancestors when the shared search changes. */
  searchQuery?: string;
}

function siblingIdsUnderParent(
  mods: VehicleDetailModification[],
  parentId: string | null
): string[] {
  return mods
    .filter((m) => {
      const pid = m.parent_modification_id != null ? String(m.parent_modification_id) : null;
      return pid === parentId;
    })
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.title.localeCompare(b.title))
    .map((m) => String(m.id));
}

function ListNode({
  node,
  showPricing,
  isOwner,
  onEdit,
  onDelete,
  onMove,
  onAddInside,
  onReorderSibling,
  allMods,
  depth,
  isLast,
  forceOpen,
  selectMode,
  selectedIds,
  onToggleSelect,
  menuOpenId,
  setMenuOpenId,
}: {
  node: HierarchyNode<VehicleDetailModification>;
  showPricing?: boolean;
  isOwner?: boolean;
  onEdit?: (modification: VehicleDetailModification) => void;
  onDelete?: (id: string) => void;
  onMove?: (modification: VehicleDetailModification) => void;
  onAddInside?: (modification: VehicleDetailModification) => void;
  onReorderSibling?: (args: { parentId: string | null; orderedIds: string[] }) => void;
  allMods: VehicleDetailModification[];
  depth: number;
  isLast: boolean;
  forceOpen: { open: boolean };
  selectMode: boolean;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  menuOpenId: string | null;
  setMenuOpenId: (id: string | null) => void;
}) {
  const mod = node.mod;
  const hasKids = node.children.length > 0;
  const childTotal = countChildren(node);
  const [open, setOpen] = useState(false);
  const indentLevels = Math.min(depth, 4);
  const id = String(mod.id);
  const selected = selectedIds.has(id);
  const menuOpen = menuOpenId === id;

  useEffect(() => {
    setOpen(forceOpen.open);
  }, [forceOpen]);

  const parentId =
    mod.parent_modification_id != null ? String(mod.parent_modification_id) : null;

  const moveSibling = (direction: 'up' | 'down' | 'top' | 'bottom') => {
    if (!onReorderSibling) return;
    const ids = siblingIdsUnderParent(allMods, parentId);
    const idx = ids.indexOf(id);
    if (idx < 0) return;
    const next = [...ids];
    next.splice(idx, 1);
    let insertAt = idx;
    if (direction === 'up') insertAt = Math.max(0, idx - 1);
    if (direction === 'down') insertAt = Math.min(ids.length - 1, idx + 1);
    if (direction === 'top') insertAt = 0;
    if (direction === 'bottom') insertAt = ids.length - 1;
    next.splice(insertAt, 0, id);
    if (next.join(',') === ids.join(',')) return;
    onReorderSibling({ parentId, orderedIds: next });
    setMenuOpenId(null);
  };

  const handleDelete = () => {
    if (onDelete && window.confirm(`Delete “${mod.title}”?`)) {
      onDelete(mod.id);
    }
    setMenuOpenId(null);
  };

  return (
    <li className="relative list-none">
      <div
        className="relative flex items-stretch build-nest-rail"
        style={{ paddingLeft: `calc(${indentLevels} * var(--build-nest-step))` }}
      >
        {depth > 0 && (
          <>
            <span
              className="absolute top-0 w-px"
              style={{
                left: `calc(${indentLevels} * var(--build-nest-step) - 0.55rem)`,
                bottom: isLast ? '50%' : 0,
                backgroundColor: 'rgba(75, 85, 99, 0.7)',
              }}
              aria-hidden
            />
            <span
              className="absolute top-[1.35rem] h-px w-2"
              style={{
                left: `calc(${indentLevels} * var(--build-nest-step) - 0.55rem)`,
                backgroundColor: 'rgba(75, 85, 99, 0.7)',
              }}
              aria-hidden
            />
          </>
        )}

        <div
          className={`flex-1 min-w-0 overflow-x-hidden flex items-start gap-1 sm:gap-2 px-2.5 py-2 sm:px-3 ${
            depth === 0 ? 'border-b border-white/[0.04]' : 'border-b border-white/[0.03]'
          } ${depth === 0 && hasKids ? 'bg-black/20' : ''} ${
            selected ? 'bg-orange-500/10' : ''
          }`}
        >
          <div className="flex shrink-0 items-start gap-0.5 mt-0.5">
            {selectMode && (
              <label className="shrink-0 flex items-center justify-center w-9 h-9">
                <span className="sr-only">Select {mod.title}</span>
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => onToggleSelect(id)}
                  className="checkbox-control"
                />
              </label>
            )}
            {hasKids ? (
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="shrink-0 flex items-center justify-center w-11 h-11 rounded-lg transition-colors text-gray-400 hover:text-orange-300"
                aria-expanded={open}
                aria-label={
                  open ? `Collapse ${mod.title}` : `Expand ${mod.title}, ${childTotal} parts inside`
                }
              >
                <svg
                  className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-90' : ''}`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            ) : !selectMode ? (
              <span className="mt-2 shrink-0 w-9 flex justify-center" aria-hidden>
                <span className="h-1.5 w-1.5 rounded-full bg-[#1a1a1a]" />
              </span>
            ) : null}
          </div>

          <ModThumb url={mod.image_url} alt="" className="mt-0.5" />

          <div className="min-w-0 flex-1 pt-1">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span
                className={
                  hasKids || depth === 0
                    ? 'text-sm sm:text-[15px] font-semibold text-white tracking-tight mod-text-wrap'
                    : 'text-sm text-gray-200 mod-text-wrap'
                }
              >
                {mod.vendor ? (
                  <>
                    <span className="text-gray-500 font-normal">{mod.vendor}</span>{' '}
                  </>
                ) : null}
                {mod.title}
              </span>
              {hasKids && (
                <span className="text-meta">
                  {childTotal} inside
                </span>
              )}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-meta">
              {depth > 0 && mod.parentComponent && <span>Under {mod.parentComponent}</span>}
              {showPricing &&
                (depth > 0 || mod.parentComponent ? (
                  <span>Included in parent</span>
                ) : mod.cost != null && mod.cost > 0 ? (
                  <span className="tabular-nums text-gray-300">{formatCurrency(mod.cost)}</span>
                ) : null)}
            </div>
          </div>

          {isOwner && !selectMode && onEdit && <button type="button" onClick={() => onEdit(mod)}
            aria-label={`Edit ${mod.title}`} className="min-h-[44px] px-2 self-center text-xs text-zinc-300 hover:text-white rounded-lg border border-white/10">Edit</button>}
          {isOwner && !selectMode && (
            <div className="relative flex shrink-0 self-center" data-mod-row-menu>
              <button
                type="button"
                onClick={() => setMenuOpenId(menuOpen ? null : id)}
                className="p-2 text-gray-500 hover:text-white rounded-lg min-h-[44px] min-w-[44px]"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label={`Actions for ${mod.title}`}
              >
                <svg className="w-4 h-4 mx-auto" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                </svg>
              </button>
              {menuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-full z-20 mt-1 w-48 rounded-xl border border-white/[0.08] bg-[#0a0a0a] shadow-xl py-1"
                >
                  {onMove && (
                    <button
                      type="button"
                      role="menuitem"
                      className="w-full text-left px-3 py-2.5 text-sm text-gray-200 hover:bg-white/[0.04]"
                      onClick={() => {
                        onMove(mod);
                        setMenuOpenId(null);
                      }}
                    >
                      Move
                    </button>
                  )}
                  {onAddInside && (
                    <button
                      type="button"
                      role="menuitem"
                      className="w-full text-left px-3 py-2.5 text-sm text-gray-200 hover:bg-white/[0.04]"
                      onClick={() => {
                        onAddInside(mod);
                        setMenuOpenId(null);
                      }}
                    >
                      Add part inside
                    </button>
                  )}
                  {onReorderSibling && (
                    <>
                      <button type="button" role="menuitem" className="w-full text-left px-3 py-2.5 text-sm text-gray-200 hover:bg-white/[0.04]" onClick={() => moveSibling('up')}>
                        Move up
                      </button>
                      <button type="button" role="menuitem" className="w-full text-left px-3 py-2.5 text-sm text-gray-200 hover:bg-white/[0.04]" onClick={() => moveSibling('down')}>
                        Move down
                      </button>
                      <button type="button" role="menuitem" className="w-full text-left px-3 py-2.5 text-sm text-gray-200 hover:bg-white/[0.04]" onClick={() => moveSibling('top')}>
                        Move to top
                      </button>
                      <button type="button" role="menuitem" className="w-full text-left px-3 py-2.5 text-sm text-gray-200 hover:bg-white/[0.04]" onClick={() => moveSibling('bottom')}>
                        Move to bottom
                      </button>
                    </>
                  )}
                  {onEdit && (
                    <button
                      type="button"
                      role="menuitem"
                      className="w-full text-left px-3 py-2.5 text-sm text-gray-200 hover:bg-white/[0.04]"
                      onClick={() => {
                        onEdit(mod);
                        setMenuOpenId(null);
                      }}
                    >
                      Edit
                    </button>
                  )}
                  {onDelete && (
                    <button
                      type="button"
                      role="menuitem"
                      className="w-full text-left px-3 py-2.5 text-sm text-red-300 hover:bg-white/[0.04]"
                      onClick={handleDelete}
                    >
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {hasKids && open && (
        <ul role="group" aria-label={`Parts in ${mod.title}`}>
          {node.children.map((child, i) => (
            <ListNode
              key={child.mod.id || child.index}
              node={child}
              showPricing={showPricing}
              isOwner={isOwner}
              onEdit={onEdit}
              onDelete={onDelete}
              onMove={onMove}
              onAddInside={onAddInside}
              onReorderSibling={onReorderSibling}
              allMods={allMods}
              depth={depth + 1}
              isLast={i === node.children.length - 1}
              forceOpen={forceOpen}
              selectMode={selectMode}
              selectedIds={selectedIds}
              onToggleSelect={onToggleSelect}
              menuOpenId={menuOpenId}
              setMenuOpenId={setMenuOpenId}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function CategorySection({
  category,
  roots,
  itemCount,
  showPricing,
  isOwner,
  onEdit,
  onDelete,
  onMove,
  onAddInside,
  onReorderSibling,
  allMods,
  forceOpen,
  defaultExpanded,
  selectMode,
  selectedIds,
  onToggleSelect,
  onSelectCategory,
  menuOpenId,
  setMenuOpenId,
}: {
  category: string;
  roots: HierarchyNode<VehicleDetailModification>[];
  itemCount: number;
  showPricing?: boolean;
  isOwner?: boolean;
  onEdit?: (modification: VehicleDetailModification) => void;
  onDelete?: (id: string) => void;
  onMove?: (modification: VehicleDetailModification) => void;
  onAddInside?: (modification: VehicleDetailModification) => void;
  onReorderSibling?: (args: { parentId: string | null; orderedIds: string[] }) => void;
  allMods: VehicleDetailModification[];
  forceOpen: { open: boolean };
  defaultExpanded: boolean;
  selectMode: boolean;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onSelectCategory: () => void;
  menuOpenId: string | null;
  setMenuOpenId: (id: string | null) => void;
}) {
  const accent = getCategoryColor(category);
  const [expanded, setExpanded] = useState(defaultExpanded);

  useEffect(() => {
    setExpanded(forceOpen.open);
  }, [forceOpen]);

  return (
    <section
      className="build-section"
      style={{
        borderColor: withAlpha(accent, 0.3),
        backgroundColor: withAlpha(accent, 0.045),
      }}
      data-category={category}
    >
      <div
        className="build-section-header"
        style={{
          borderBottomColor: withAlpha(accent, 0.22),
          background: `linear-gradient(90deg, ${withAlpha(accent, 0.16)} 0%, ${withAlpha(accent, 0.06)} 52%, rgba(10, 10, 10, 0.45) 100%)`,
        }}
      >
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex-1 flex items-center gap-3 text-left min-h-[40px] cursor-pointer"
          aria-expanded={expanded}
        >
          <span
            className="shrink-0 flex items-center justify-center w-11 h-11 rounded-lg border bg-black/30"
            style={{
              borderColor: withAlpha(accent, 0.35),
              backgroundColor: withAlpha(accent, 0.1),
            }}
            aria-hidden
          >
            <CategoryIcon category={category} size="sm" color={accent} />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="heading-card text-base">{category}</h3>
            <p className="text-meta mt-0.5">
              {itemCount} part{itemCount === 1 ? '' : 's'}
              {roots.some((r) => r.children.length > 0) ? ' · nested parts' : ''}
            </p>
          </div>
          <span
            className={`shrink-0 text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`}
            aria-hidden
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </span>
        </button>
        {selectMode && (
          <button
            type="button"
            onClick={onSelectCategory}
            className="shrink-0 text-xs text-orange-300/90 hover:text-orange-200 px-2 py-2 min-h-[40px] cursor-pointer"
          >
            Select all
          </button>
        )}
      </div>

      {expanded && (
        <div className="border-t border-white/[0.06]">
          <ul className="relative">
            {roots.map((node, i) => (
              <ListNode
                key={node.mod.id || node.index}
                node={node}
                showPricing={showPricing}
                isOwner={isOwner}
                onEdit={onEdit}
                onDelete={onDelete}
                onMove={onMove}
                onAddInside={onAddInside}
                onReorderSibling={onReorderSibling}
                allMods={allMods}
                depth={0}
                isLast={i === roots.length - 1}
                forceOpen={forceOpen}
                selectMode={selectMode}
                selectedIds={selectedIds}
                onToggleSelect={onToggleSelect}
                menuOpenId={menuOpenId}
                setMenuOpenId={setMenuOpenId}
              />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/**
 * List view: primary owner management surface + visitor-readable nested list.
 */
export const ModificationNestedList: React.FC<ModificationNestedListProps> = ({
  modifications,
  allModifications,
  showPricing,
  isOwner,
  onEdit,
  onDelete,
  onMove,
  onAddInside,
  onReorderSibling,
  onExport,
  selectMode = false,
  onSelectModeChange,
  selectedIds,
  onSelectedIdsChange,
  onBulkMove,
  onBulkCategory,
  onBulkDelete,
  onBulkExport,
  searchQuery = '',
}) => {
  const sections = useMemo(() => buildCategoryForest(modifications), [modifications]);
  const allMods = allModifications || modifications;
  const hasNesting = useMemo(
    () => sections.some((s) => s.roots.some((r) => r.children.length > 0)),
    [sections]
  );
  const [forceOpen, setForceOpen] = useState(() => ({ open: Boolean(searchQuery.trim()) }));
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const selected = selectedIds || new Set<string>();

  useEffect(() => {
    setForceOpen({ open: Boolean(searchQuery.trim()) });
    setMenuOpenId(null);
  }, [searchQuery]);

  useEffect(() => {
    if (!menuOpenId) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpenId(null);
    };
    const onPointer = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest('[data-mod-row-menu]')) return;
      setMenuOpenId(null);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [menuOpenId]);

  const visibleIds = useMemo(
    () => modifications.map((m) => String(m.id)).filter(Boolean),
    [modifications]
  );

  const setSelected = (next: Set<string>) => {
    onSelectedIdsChange?.(next);
  };

  const toggleSelect = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const selectVisible = () => setSelected(new Set(visibleIds));
  const clearSelection = () => setSelected(new Set());

  const collectIdsInForest = (roots: HierarchyNode<VehicleDetailModification>[]): string[] => {
    const out: string[] = [];
    const walk = (n: HierarchyNode<VehicleDetailModification>) => {
      out.push(String(n.mod.id));
      n.children.forEach(walk);
    };
    roots.forEach(walk);
    return out;
  };

  return (
    <div className="space-y-4 build-views-shell min-w-0 overflow-x-hidden">
      <div className="flex flex-col gap-3 min-w-0">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 min-w-0">
          <p className="hidden md:block text-xs text-gray-500 max-w-lg leading-relaxed break-words">
            {isOwner
              ? 'Organize your build here. Move parts, nest pieces, and copy a clean list.'
              : 'Parts grouped by category. Nested pieces stay under their parent.'}
          </p>
          <div className="flex flex-wrap gap-2">
            {(hasNesting || sections.length > 1) && (
              <button
                type="button"
                onClick={() => setForceOpen((v) => ({ open: !v.open }))}
                className="shrink-0 btn-ghost btn-sm"
              >
                {forceOpen.open ? 'Collapse all' : 'Expand all'}
              </button>
            )}
            {isOwner && onExport && (
              <button
                type="button"
                onClick={onExport}
                className="btn-secondary btn-sm"
              >
                <span className="md:hidden">Export</span>
                <span className="hidden md:inline">Copy or export</span>
              </button>
            )}
            {isOwner && onSelectModeChange && (
              <button
                type="button"
                onClick={() => {
                  onSelectModeChange(!selectMode);
                  if (selectMode) clearSelection();
                }}
                className={`btn-sm min-h-[44px] md:min-h-[40px] px-3 rounded-lg border cursor-pointer ${
                  selectMode
                    ? 'border-orange-500/40 text-orange-300 bg-orange-500/10'
                    : 'border-white/[0.08] text-gray-200 hover:bg-white/[0.04]'
                }`}
                aria-pressed={selectMode}
              >
                {selectMode ? 'Done' : 'Select'}
              </button>
            )}
          </div>
        </div>

        {isOwner && selectMode && (
          <div
            className="sticky top-16 z-20 lg:static rounded-xl border border-white/[0.08] bg-[#0a0a0a]/95 backdrop-blur px-3 py-3 flex flex-wrap items-center gap-2"
            role="toolbar"
            aria-label="Bulk part actions"
          >
            <span className="text-sm text-white font-medium tabular-nums">
              {selected.size} selected
            </span>
            <button
              type="button"
              onClick={selectVisible}
              className="text-xs min-h-[44px] md:min-h-[40px] px-2 text-gray-300 cursor-pointer"
            >
              <span className="md:hidden">All</span>
              <span className="hidden md:inline">Select all visible</span>
            </button>
            <button
              type="button"
              onClick={clearSelection}
              className="text-xs min-h-[44px] md:min-h-[40px] px-2 text-gray-400 cursor-pointer"
            >
              Clear
            </button>
            <span className="hidden md:inline w-px h-6 bg-[#161616] mx-1" aria-hidden />
            {onBulkMove && (
              <button
                type="button"
                disabled={selected.size === 0}
                onClick={onBulkMove}
                className="btn-primary btn-sm disabled:opacity-40"
              >
                Move
              </button>
            )}
            {onBulkCategory && (
              <button
                type="button"
                disabled={selected.size === 0}
                onClick={onBulkCategory}
                className="btn-secondary btn-sm disabled:opacity-40"
              >
                <span className="md:hidden">Category</span>
                <span className="hidden md:inline">Change category</span>
              </button>
            )}
            {onBulkExport && (
              <button
                type="button"
                disabled={selected.size === 0}
                onClick={onBulkExport}
                className="btn-ghost btn-sm disabled:opacity-40 hidden sm:inline-flex"
              >
                Export
              </button>
            )}
            {onBulkDelete && (
              <button
                type="button"
                disabled={selected.size === 0}
                onClick={onBulkDelete}
                className="btn-danger btn-sm disabled:opacity-40"
              >
                <span className="md:hidden">Delete</span>
                <span className="hidden md:inline">Delete selected</span>
              </button>
            )}
          </div>
        )}
      </div>

      <div className="space-y-3.5" onClick={() => menuOpenId && setMenuOpenId(null)}>
        {sections.map((section, index) => (
          <CategorySection
            key={section.category}
            category={section.category}
            roots={section.roots}
            itemCount={section.itemCount}
            showPricing={showPricing}
            isOwner={isOwner}
            onEdit={onEdit}
            onDelete={onDelete}
            onMove={onMove}
            onAddInside={onAddInside}
            onReorderSibling={onReorderSibling}
            allMods={allMods}
            forceOpen={forceOpen}
            defaultExpanded={false}
            selectMode={!!selectMode}
            selectedIds={selected}
            onToggleSelect={toggleSelect}
            onSelectCategory={() => {
              const ids = collectIdsInForest(section.roots);
              const next = new Set(selected);
              ids.forEach((id) => next.add(id));
              setSelected(next);
            }}
            menuOpenId={menuOpenId}
            setMenuOpenId={setMenuOpenId}
          />
        ))}
      </div>
    </div>
  );
};

export default ModificationNestedList;
